import {
  createFilterDTOSchema,
  createPageQueryDTOSchema,
  DEFAULT_PAGE_LIMIT,
  timestampDTOSchema,
} from '@tempo-io/common-dto';
import { z } from 'zod';
import { enabledConnectionSchema } from './auth-pipeline-dto';

export const roles = ['admin', 'orchestrator', 'visualizer', 'contributor'] as const;
export const roleSchema = z.enum(roles);

export const assignableRoleSchema = roleSchema.exclude(['admin']);

export const inviteRoleSchema = assignableRoleSchema;

export const userCreateDTOSchema = z.object({
  auth0Id: z.string().nonempty(),
  email: z.email().toLowerCase(),
  name: z.string().optional(),
  givenName: z.string().optional(),
  familyName: z.string().optional(),
  pictureUrl: z.string().optional(),
  locale: z.string().optional(),
  blocked: z.boolean(),
});

export const userDTOSchema = z.object({
  id: z.string().nonempty(),
  auth0Id: z.string().nonempty(),
  email: z.email(),
  name: z
    .string()
    .nullish()
    .transform((v) => v ?? undefined)
    .pipe(z.string().optional()),
  pictureUrl: z
    .string()
    .nullish()
    .transform((v) => v ?? undefined)
    .pipe(z.string().optional()),
  locale: z
    .string()
    .nullish()
    .transform((v) => v ?? undefined)
    .pipe(z.string().optional()),
  blocked: z.boolean(),

  lastLogin: z.string().optional(),
  ...timestampDTOSchema.shape,
});

export const organizationInternalUserDTOSchema = userDTOSchema.extend({
  loginCount: z.number().nullable().optional(),
});

const nameRegex = /^[\p{L}\p{M}\p{N}.'\- ]+$/u;
const noConsecutiveSpacesRegex = /^(?!.*\s{2,}).*$/;

export const userUpdateDTOSchema = z.object({
  name: z
    .string()
    .max(150, { message: 'Name is too long' })
    .min(1, { message: 'Name cannot be empty or just spaces' })
    .regex(nameRegex, { message: 'Name contains invalid characters' })
    .regex(noConsecutiveSpacesRegex, { message: 'Name cannot contain multiple consecutive spaces' })
    .optional(),
  pictureUrl: z.string().optional(),
});

export const userAndRolesDTOSchema = userDTOSchema.extend({
  roles: z.array(roleSchema),
});

export const userInviteDTOSchema = z.object({
  id: z.coerce.number(),
  inviteeEmail: z.email(),
  inviteeUserId: z.string().nullish(),
  roles: z.array(roleSchema),
  createdAt: z.string(),
  expiresAt: z.string(),
  inviterUserId: z.string(),
});

export const pendingInviteDTOSchema = z.object({
  id: z.number(),
  inviteeEmail: z.email(),
  role: roleSchema,
  expiresAt: z.string(),
  createdAt: z.string(),
  organization: z.object({
    id: z.string().nonempty(),
    auth0Id: z.string().nonempty(),
    name: z.string().nonempty(),
    displayName: z.string().nonempty(),
    createdAt: z.string(),
    updatedAt: z.string(),
  }),
  inviter: z.object({
    id: z.string().nonempty(),
    name: z.string().nullable(),
    email: z.string(),
    pictureUrl: z.string().nullable(),
  }),
  samlConnectionName: z.string().nullish(),
  existingOrgAuth0Id: z.string().nullish(),
});

// Single-invite lookup (auth-pipeline GET /invite) additionally resolves the invited
// org's enabled connections so the client can decide whether to switch in silently or
// re-authenticate. The list endpoint does not carry this, so it stays off the base schema.
export const pendingInviteWithConnectionsDTOSchema = pendingInviteDTOSchema.extend({
  enabledConnectionNames: z.array(z.string()),
  // The org's non-SSO connections with their Auth0 strategy, so the client can render a
  // sign-in method picker when a single link connection can't be inferred.
  enabledConnections: z.array(enabledConnectionSchema),
  // The invitee's own Auth0 connection name(s). When the account exists but has no gate-org
  // membership (an orphan), there is no org to log into, so the client authenticates via one
  // of these in a non-org context before accepting. Empty when the invitee has no account yet.
  userConnectionNames: z.array(z.string()),
});

export const { baseFilterSchema: userFilterBaseSchema, filterSchema: userFilterSchema } =
  createFilterDTOSchema({
    id: 'string',
    email: 'string',
    name: 'string',
    role: 'string',
    lastLogin: 'date',
  });

export const {
  baseFilterSchema: userInviteFilterBaseSchema,
  filterSchema: userInviteFilterSchema,
} = createFilterDTOSchema({
  inviteeEmail: 'string',
  inviteeUserId: 'string',
  role: 'string',
  createdAt: 'date',
  expiresAt: 'date',
});

export const userSortableFields = ['id', 'email', 'lastLogin', 'name'] as const;
export const userInviteSortableFields = ['id', 'inviteeEmail', 'createdAt', 'expiresAt'] as const;

const INVALID_JSON = 'invalid_json';
const filterPreProcessSchema = z.union([userFilterSchema, z.literal(INVALID_JSON)]);
const userInviteFilterPreProcessSchema = z.union([userInviteFilterSchema, z.literal(INVALID_JSON)]);

export const userFilterJSONDTOSchema = z
  .preprocess((val) => {
    if (typeof val !== 'string') return val;
    try {
      return JSON.parse(val);
    } catch {
      return INVALID_JSON;
    }
  }, filterPreProcessSchema)
  .refine((val): val is Exclude<typeof val, typeof INVALID_JSON> => val !== INVALID_JSON, {
    message: 'Must be valid JSON',
  })
  .check(({ value, issues }) => {
    const { error } = userFilterSchema.safeParse(value);
    if (error) {
      // biome-ignore lint/suspicious/noExplicitAny: suppressing type error
      issues.push(...(error.issues as any[]));
    }
  })
  .transform((val) => userFilterSchema.parse(val));

const userFilterDTOSchema = z.object({
  filter: userFilterJSONDTOSchema.optional(),
});

export const userInviteFilterJSONDTOSchema = z
  .preprocess((val) => {
    if (typeof val !== 'string') return val;
    try {
      return JSON.parse(val);
    } catch {
      return INVALID_JSON;
    }
  }, userInviteFilterPreProcessSchema)
  .refine((val): val is Exclude<typeof val, typeof INVALID_JSON> => val !== INVALID_JSON, {
    message: 'Must be valid JSON',
  })
  .check(({ value, issues }) => {
    const { error } = userInviteFilterSchema.safeParse(value);
    if (error) {
      // biome-ignore lint/suspicious/noExplicitAny: suppressing type error
      issues.push(...(error.issues as any[]));
    }
  })
  .transform((val) => userInviteFilterSchema.parse(val));

export const userInviteFilterDTOSchema = z.object({
  filter: userInviteFilterJSONDTOSchema.optional(),
});

export const userCountInputDTOSchema = userFilterDTOSchema.optional();
export const userInviteCountInputDTOSchema = userInviteFilterDTOSchema.optional();

export const userSearchInputDTOSchema = z
  .object({
    filter: userFilterJSONDTOSchema.optional(),
    ...createPageQueryDTOSchema({ sortableFields: userSortableFields, defaultSortBy: 'email' })
      .shape,
  })
  .optional()
  .default({ sortOrder: 'asc', sortBy: 'email', limit: DEFAULT_PAGE_LIMIT });

export const userInviteSearchInputDTOSchema = z
  .object({
    filter: userInviteFilterJSONDTOSchema.optional(),
    ...createPageQueryDTOSchema({
      sortableFields: userInviteSortableFields,
      defaultSortBy: 'createdAt',
      defaultSortOrder: 'desc',
    }).shape,
  })
  .optional()
  .default({ sortOrder: 'desc', sortBy: 'createdAt', limit: DEFAULT_PAGE_LIMIT });

const internalUserSearchSortableFields = ['id', 'createdAt', 'updatedAt'] as const;

export const { filterSchema: internalUserSearchFilterSchema } = createFilterDTOSchema({
  createdAt: 'date',
  updatedAt: 'date',
});

export const internalUserSearchInputDTOSchema = z
  .object({
    filter: internalUserSearchFilterSchema,
    ...createPageQueryDTOSchema({
      sortableFields: internalUserSearchSortableFields,
      defaultSortBy: 'createdAt',
    }).shape,
  })
  .optional()
  .default({ sortOrder: 'asc', sortBy: 'createdAt', limit: DEFAULT_PAGE_LIMIT });

export const m2mInviteAcceptedInputDTOSchema = z.object({
  inviteeEmail: z.email().toLowerCase(),
  inviteeUserId: z.string(),
  organizationAuth0Id: z.string(),
});

export const userAcceptInviteInputDTOSchema = z.object({
  inviteId: z.number(),
});
